import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import {
  boatWorldcatAerial,
  groupWsbCatch,
  heroBluefinDeck,
  heroWsbKelp,
} from '../../assets/images/hero/imageAssets';
import SEOHead from '../../components/SEOHead';
import { getOgImageUrl, OG_IMAGE_DIMENSIONS } from '../../utils/seoSchemas';
import {
  BodyText,
  CalendarCell,
  CalendarContainer,
  CalendarHeader,
  CalendarLegend,
  CalendarRow,
  CalendarSection,
  CalendarSpecies,
  CalendarTable,
  CalendarWrapper,
  CategoryCard,
  CategoryContainer,
  CategoryContent,
  CategoryCta,
  CategoryDescription,
  CategoryGrid,
  CategoryImage,
  CategorySection,
  CategoryTagline,
  CategoryTitle,
  CtaButton,
  CtaContainer,
  CtaSection,
  CtaSubtext,
  CtaTitle,
  HeroButtons,
  HeroInner,
  HeroSubtitle,
  HeroTitle,
  LegendDot,
  LegendItem,
  LocationCard,
  LocationDescription,
  LocationDistance,
  LocationName,
  LocationsContainer,
  LocationsGrid,
  LocationsSection,
  LocationSpecies,
  PageHero,
  PrimaryButton,
  SectionEyebrow,
  SectionTitle,
  SeasonIndicator,
  SpeciesTag,
} from './styles/ChartersOverview.styles';

// Scroll-triggered wrapper
const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

type SeasonStatus = 'peak' | 'available' | 'off';

interface SpeciesSeasonData {
  name: string;
  months: SeasonStatus[];
}

const SEASON_DATA: SpeciesSeasonData[] = [
  {
    name: 'White Seabass',
    months: ['off', 'off', 'available', 'available', 'peak', 'peak', 'peak', 'available', 'available', 'off', 'off', 'off'],
  },
  {
    name: 'Yellowtail',
    months: ['off', 'off', 'off', 'available', 'available', 'peak', 'peak', 'peak', 'peak', 'available', 'off', 'off'],
  },
  {
    name: 'Bluefin Tuna',
    months: ['off', 'off', 'off', 'off', 'available', 'available', 'peak', 'peak', 'peak', 'peak', 'available', 'off'],
  },
  {
    name: 'Calico Bass',
    months: ['available', 'available', 'available', 'peak', 'peak', 'peak', 'peak', 'peak', 'peak', 'peak', 'available', 'available'],
  },
  {
    name: 'Halibut',
    months: ['available', 'available', 'available', 'available', 'peak', 'peak', 'peak', 'peak', 'available', 'available', 'available', 'available'],
  },
  {
    name: 'Dorado',
    months: ['off', 'off', 'off', 'off', 'off', 'off', 'available', 'peak', 'peak', 'peak', 'available', 'off'],
  },
  {
    name: 'Sheephead',
    months: ['available', 'available', 'available', 'available', 'peak', 'peak', 'peak', 'peak', 'peak', 'available', 'available', 'available'],
  },
];

const LOCATIONS = [
  {
    name: 'Catalina Island',
    distance: '26 mi from port / 60-90 min',
    species: ['White Seabass', 'Yellowtail', 'Calico Bass', 'Halibut'],
    description: 'World-class kelp forests, rocky reefs, and blue water drop-offs. The crown jewel of SoCal spearfishing and sportfishing.',
  },
  {
    name: 'San Clemente Island',
    distance: '55 mi from port / 2-3 hr',
    species: ['White Seabass', 'Yellowtail', 'Bluefin Tuna'],
    description: 'Remote military island with pristine reefs and minimal pressure. Big fish, clean water, and serious hunting.',
  },
  {
    name: 'Horseshoe Kelp',
    distance: 'Nearshore / 20-40 min',
    species: ['White Seabass', 'Calico Bass', 'Halibut', 'Sheephead'],
    description: 'Massive nearshore kelp complex — one of the most productive structures in SoCal. Ideal for half-day trips.',
  },
  {
    name: 'Palos Verdes Reefs',
    distance: 'Nearshore / 15-30 min',
    species: ['Calico Bass', 'Sheephead', 'Halibut'],
    description: 'Rocky reef structure right off the Palos Verdes Peninsula. Quick access, consistent action, and great for kelp diving.',
  },
  {
    name: 'Offshore Kelp Paddies',
    distance: 'Variable / seasonal',
    species: ['Yellowtail', 'Dorado', 'Bluefin Tuna'],
    description: 'Drifting kelp mats that attract pelagic species offshore. When the paddies show up, the fishing is world-class.',
  },
  {
    name: 'Oil Rig Structure',
    distance: '15-30 mi from port / 45-90 min',
    species: ['Yellowtail', 'Calico Bass', 'Sheephead'],
    description: 'Artificial reef systems that stack up fish. Vertical structure from surface to 200+ feet — perfect for both spearfishing and sportfishing.',
  },
];

const CATEGORIES = [
  {
    image: heroWsbKelp,
    tagline: 'Dive in. Hunt deep. Earn your fish.',
    title: 'Spearfishing Charters',
    description: 'Freedive spearfishing across Southern California — Catalina, San Clemente Island, kelp forests, reefs, and open blue water. Captain-guided, small-group trips.',
    link: '/charters/spearfishing',
  },
  {
    image: heroBluefinDeck,
    tagline: 'Rod and reel. Big targets. Captain-led.',
    title: 'Sportfishing Charters',
    description: 'Rod-and-reel action targeting Bluefin Tuna, Yellowtail, Dorado, and more. Expert local knowledge and top-tier tackle.',
    link: '/charters/sportfishing',
  },
  {
    image: groupWsbCatch,
    tagline: 'Your boat. Your crew. Your call.',
    title: 'Private Charters',
    description: 'Book the entire boat for your group. Custom itinerary, flexible schedule — spearfishing, sportfishing, or both. Your trip, your way.',
    link: '/charters/private',
  },
];

const ChartersOverviewPage = () => {
  return (
    <>
      <SEOHead
        title="Charters — Spearfishing & Sportfishing | Fussaki"
        description="Explore spearfishing, sportfishing, and private charter options across Southern California. Catalina Island, San Clemente Island, kelp forests, open blue water, and more."
        canonical="https://fussaki.com/charters"
        ogImage={getOgImageUrl(boatWorldcatAerial)}
        ogImageWidth={OG_IMAGE_DIMENSIONS.boatWorldcatAerial.width}
        ogImageHeight={OG_IMAGE_DIMENSIONS.boatWorldcatAerial.height}
      />

      {/* ─── Hero ─── */}
      <PageHero $bgImage={boatWorldcatAerial} role="img" aria-label="Aerial view of the WorldCat 295CC charter vessel cruising Southern California waters">
        <HeroInner>
          <HeroTitle>Spearfishing &amp; <span>Sportfishing Charters</span></HeroTitle>
          <HeroSubtitle>
            Premium captain-led charters out of San Pedro, California. From Catalina
            Island to San Clemente, offshore blue water to nearshore kelp forests —
            Captain Christos puts you on fish across Southern California's best waters.
          </HeroSubtitle>
          <HeroButtons>
            <PrimaryButton to="/charters/spearfishing">
              Spearfishing Charters {FaArrowRight({ size: 14 })}
            </PrimaryButton>
            <PrimaryButton to="/book">
              Book Your Trip {FaArrowRight({ size: 14 })}
            </PrimaryButton>
          </HeroButtons>
        </HeroInner>
      </PageHero>

      {/* ─── Category Cards ─── */}
      <CategorySection>
        <CategoryContainer>
          <AnimatedSection>
            <SectionEyebrow style={{ textAlign: 'center' }}>Our Charters</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              Pick Your Mission
            </SectionTitle>
          </AnimatedSection>

          <CategoryGrid>
            {CATEGORIES.map((cat, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <CategoryCard to={cat.link}>
                  <CategoryImage $bgImage={cat.image} />
                  <CategoryContent>
                    <CategoryTagline>{cat.tagline}</CategoryTagline>
                    <CategoryTitle>{cat.title}</CategoryTitle>
                    <CategoryDescription>{cat.description}</CategoryDescription>
                    <CategoryCta>
                      Explore {FaArrowRight({ size: 12 })}
                    </CategoryCta>
                  </CategoryContent>
                </CategoryCard>
              </AnimatedSection>
            ))}
          </CategoryGrid>
        </CategoryContainer>
      </CategorySection>

      {/* ─── Where We Fish ─── */}
      <LocationsSection>
        <LocationsContainer>
          <AnimatedSection>
            <SectionEyebrow style={{ textAlign: 'center' }}>Where We Fish</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              Southern California's Best Waters
            </SectionTitle>
            <BodyText style={{ textAlign: 'center', maxWidth: 650, margin: '0 auto' }}>
              Every trip is conditions-driven. Captain Christos reads the swell, current,
              visibility, and water temp — then picks the spot that gives you the best shot.
            </BodyText>
          </AnimatedSection>

          <LocationsGrid>
            {LOCATIONS.map((loc, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <LocationCard>
                  <LocationName>{loc.name}</LocationName>
                  <LocationDistance>{loc.distance}</LocationDistance>
                  <LocationDescription>{loc.description}</LocationDescription>
                  <LocationSpecies>
                    {loc.species.map((sp) => (
                      <SpeciesTag key={sp}>{sp}</SpeciesTag>
                    ))}
                  </LocationSpecies>
                </LocationCard>
              </AnimatedSection>
            ))}
          </LocationsGrid>
        </LocationsContainer>
      </LocationsSection>

      {/* ─── Species Season Calendar ─── */}
      <CalendarSection>
        <CalendarContainer>
          <AnimatedSection>
            <SectionEyebrow style={{ textAlign: 'center' }}>Season Calendar</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              When to Target Each Species
            </SectionTitle>
            <BodyText style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
              Southern California offers year-round fishing, but each species has a prime
              window. Plan your trip around your target.
            </BodyText>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            <CalendarWrapper>
              <CalendarTable>
                <thead>
                  <tr>
                    <CalendarHeader>Species</CalendarHeader>
                    {MONTHS.map((m) => (
                      <CalendarHeader key={m}>{m}</CalendarHeader>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SEASON_DATA.map((species) => (
                    <CalendarRow key={species.name}>
                      <CalendarSpecies>{species.name}</CalendarSpecies>
                      {species.months.map((status, j) => (
                        <CalendarCell key={j} $status={status}>
                          <SeasonIndicator $status={status} />
                        </CalendarCell>
                      ))}
                    </CalendarRow>
                  ))}
                </tbody>
              </CalendarTable>
            </CalendarWrapper>

            <CalendarLegend>
              <LegendItem>
                <LegendDot $status="peak" />
                Peak Season
              </LegendItem>
              <LegendItem>
                <LegendDot $status="available" />
                Available
              </LegendItem>
              <LegendItem>
                <LegendDot $status="off" />
                Off Season
              </LegendItem>
            </CalendarLegend>
          </AnimatedSection>
        </CalendarContainer>
      </CalendarSection>

      {/* ─── CTA ─── */}
      <CtaSection>
        <AnimatedSection>
          <CtaContainer>
            <SectionEyebrow style={{ textAlign: 'center' }}>Ready?</SectionEyebrow>
            <CtaTitle>Ready to Book?</CtaTitle>
            <CtaSubtext>
              Check live availability, pick your date, and lock in your spot.
              Open party and private charter options available.
            </CtaSubtext>
            <CtaButton to="/book">
              Book Your Trip {FaArrowRight({ size: 16 })}
            </CtaButton>
          </CtaContainer>
        </AnimatedSection>
      </CtaSection>
    </>
  );
};

export default ChartersOverviewPage;
